import {
  refreshComplianceReport,
  useFetchFindoraDashboardListQuery,
  useFetchSharedComplianceListQuery,
  useFetchSharedComplianceQuery,
} from 'features/private-views/findora-dashboard/findoraDashboardListApiSlice'
import {
  DENSITY_COLORS,
  getLatLng,
  RISK_COLORS,
} from 'components/Map'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { getScoreTextColor } from 'utility/Utils'
import {
  ESG_NA_COLOR,
  esgScoresByUser,
} from './layers'

export default function useLayerData({
  layers,
  isShared,
  sharedResourceId,
  members,
  mapData,
  esgMonth,
  esgMetric,
}) {
  // A shared group's ESG is readable when its owner also shared their
  // COMPLIANCE resource with us; both resourceIds are the owner's userId.
  const sharedEsgList = useFetchSharedComplianceListQuery(undefined, {
    skip: !isShared,
  })
  const hasSharedEsg = Boolean(
    sharedEsgList.data?.items?.some(
      (it) => String(it?.resourceId) === String(sharedResourceId),
    ),
  )
  const disabledReason = {
    esg:
      !isShared || hasSharedEsg
        ? null
        : sharedEsgList.isFetching
          ? 'Memeriksa akses ESG...'
          : 'ESG grup ini belum dibagikan ke Anda',
  }
  const esgOn = layers.esg && !disabledReason.esg
  const hotspotOn = esgOn && layers.esgHotspot

  // --- raw data ---
  const esgDate = moment(esgMonth)
  const esgParams = { month: esgDate.month() + 1, year: esgDate.format('YYYY') }
  const ownEsg = useFetchFindoraDashboardListQuery(esgParams, {
    skip: !esgOn || isShared,
  })
  const sharedEsg = useFetchSharedComplianceQuery(
    { ...esgParams, resourceId: sharedResourceId },
    { skip: !esgOn || !isShared },
  )
  const esg = isShared ? sharedEsg : ownEsg
  const dispatch = useDispatch()
  const refreshEsg = () =>
    dispatch(
      isShared
        ? refreshComplianceReport('fetchSharedCompliance', {
            ...esgParams,
            resourceId: sharedResourceId,
          })
        : refreshComplianceReport('fetchFindoraDashboardList', esgParams),
    )
  const esgScores = useMemo(() => esgScoresByUser(esg.data), [esg.data])

  // --- derived views ---
  const memberPosition = useMemo(() => {
    const pos = {}
    mapData.forEach((el) => {
      if (pos[el?.userId]) return
      const p = getLatLng(el)
      if (p) pos[el.userId] = p
    })
    return pos
  }, [mapData])

  const scoreOf = (userId) => esgScores.get(String(userId))?.[esgMetric]
  const colorFor = esgOn
    ? (el) => {
        const score = scoreOf(el?.userId)
        return typeof score === 'number' ? getScoreTextColor(score) : ESG_NA_COLOR
      }
    : undefined

  const overlays = useMemo(() => [], [])

  // Only one heatmap is ever on (see applyLayerRules); pick whichever it is.
  const heatmap = useMemo(() => {
    if (hotspotOn) {
      // Weight by how far below 100 a member scores, relative to the worst
      // member, so red = clusters of low scores whatever the overall level.
      const scoredPoints = members
        .map((m) => {
          const p = memberPosition[m?.userId]
          const score = esgScores.get(String(m?.userId))?.[esgMetric]
          return p && typeof score === 'number' ? { ...p, gap: 100 - score } : null
        })
        .filter(Boolean)
      const maxGap = Math.max(1, ...scoredPoints.map((p) => p.gap))
      const points = scoredPoints.map(({ gap, ...p }) => ({
        ...p,
        weight: gap / maxGap,
      }))
      return { key: 'esg', points, colors: RISK_COLORS }
    }
    if (layers.density) {
      return {
        key: 'density',
        points: mapData.map(getLatLng).filter(Boolean),
        colors: DENSITY_COLORS,
      }
    }
    return null
  }, [
    hotspotOn,
    layers.density,
    members,
    memberPosition,
    esgScores,
    esgMetric,
    mapData,
  ])

  const scored = members
    .map((m) => scoreOf(m?.userId))
    .filter((s) => typeof s === 'number')

  return {
    colorFor,
    overlays,
    heatmap,
    disabledReason,
    refreshEsg,
    status: {
      esg: {
        loading: esg.isFetching,
        error: esg.isError,
        needsPermission: Boolean(
          esg.data?.requireDetailPermission && !esgScores.size,
        ),
        matched: scored.length,
        total: members.length,
        average: scored.length
          ? scored.reduce((a, b) => a + b, 0) / scored.length
          : null,
      },
    },
  }
}
